import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { useTranslation } from "react-i18next";
import { useEffect } from "react";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { LANGUAGES } from "./i18n";

// Pages
import LoginPage from "./pages/LoginPage";
import RegisterPage from "./pages/RegisterPage";
import AdminDashboard from "./pages/AdminDashboard";
import AdminCustomerDetailPage from "./pages/AdminCustomerDetailPage";
import AdminTenantsPage from "./pages/AdminTenantsPage";
import CustomerDashboard from "./pages/CustomerDashboard";
import TenantDashboard from "./pages/TenantDashboard";
import ApartmentsPage from "./pages/ApartmentsPage";
import UnitsPage from "./pages/UnitsPage";
import DevicesPage from "./pages/DevicesPage";
import TenantsPage from "./pages/TenantsPage";
import TariffsPage from "./pages/TariffsPage";
import ReportsPage from "./pages/ReportsPage";
import TopupPage from "./pages/TopupPage";
import ProfilePage from "./pages/ProfilePage";
import AdminFinancePage from "./pages/AdminFinancePage";
import AdminActivityPage from "./pages/AdminActivityPage";
import PermissionTemplatesPage from "./pages/PermissionTemplatesPage";
import AdminSubscriptionPage from "./pages/AdminSubscriptionPage";
import CustomerSubscriptionPage from "./pages/CustomerSubscriptionPage";
import CashHandoverPage from "./pages/CashHandoverPage";
import AdminCustomersPage from "./pages/AdminCustomersPage";
import DeviceDetailPage from "./pages/DeviceDetailPage";
import AdminTariffsPage from "./pages/AdminTariffsPage";
import DeviceAlertsPage from "./pages/DeviceAlertsPage";
import DeviceHealthPage from "./pages/DeviceHealthPage";
import InvoicesPage from "./pages/InvoicesPage";
import CustomerSettingsPage from "./pages/CustomerSettingsPage";
import FixedExpensesPage from "./pages/FixedExpensesPage";
import TenantInvoicesPage from "./pages/TenantInvoicesPage";
import TopupConfirmPage from "./pages/TopupConfirmPage";
import StaffPage from "./pages/StaffPage";
import CustomerActivityPage from "./pages/CustomerActivityPage";
import DeletedInvoicesPage from "./pages/DeletedInvoicesPage";
import QueuePage from "./pages/QueuePage";
import PropertiesPage from "./pages/PropertiesPage";
import PropertyDetailPage from "./pages/PropertyDetailPage";

// Layout
import DashboardLayout from "./components/DashboardLayout";

// Protected Route Component
const ProtectedRoute = ({ children, allowedRoles }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="animate-pulse text-indigo-600">Yükleniyor...</div>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  
  if (allowedRoles && !allowedRoles.includes(user.role)) {
    if (user.role === "admin") return <Navigate to="/admin" replace />;
    if (user.role === "customer") return <Navigate to="/dashboard" replace />;
    if (user.role === "staff") return <Navigate to="/dashboard" replace />;
    if (user.role === "tenant") return <Navigate to="/tenant" replace />;
    return <Navigate to="/login" replace />;
  }
  
  return children;
};

// Public Route
const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="animate-pulse text-indigo-600">Yükleniyor...</div>
      </div>
    );
  }
  
  if (user) {
    if (user.role === "admin") return <Navigate to="/admin" replace />;
    if (user.role === "customer") return <Navigate to="/dashboard" replace />;
    if (user.role === "staff") return <Navigate to="/dashboard" replace />;
    if (user.role === "tenant") return <Navigate to="/tenant" replace />;
  }
  
  return children;
};

function RtlManager() {
  const { i18n } = useTranslation();
  useEffect(() => {
    const lang = LANGUAGES.find((l) => l.code === i18n.language);
    document.documentElement.dir = lang?.dir === "rtl" ? "rtl" : "ltr";
    document.documentElement.lang = i18n.language;
  }, [i18n.language]);
  return null;
}

function App() {
  return (
    <AuthProvider>
      <RtlManager />
      <BrowserRouter>
        <Routes>
          {/* Public Routes */}
          <Route path="/login" element={<PublicRoute><LoginPage /></PublicRoute>} />
          <Route path="/register" element={<PublicRoute><RegisterPage /></PublicRoute>} />
          {/* Token-based topup confirm - no auth required */}
          <Route path="/topup-confirm/:token" element={<TopupConfirmPage />} />
          
          {/* Admin Routes */}
          <Route path="/admin" element={
            <ProtectedRoute allowedRoles={["admin"]}>
              <DashboardLayout><AdminDashboard /></DashboardLayout>
            </ProtectedRoute>
          } />
          <Route path="/admin/customers/:customerId" element={
            <ProtectedRoute allowedRoles={["admin"]}>
              <DashboardLayout><AdminCustomerDetailPage /></DashboardLayout>
            </ProtectedRoute>
          } />
          <Route path="/admin/tenants" element={
            <ProtectedRoute allowedRoles={["admin"]}>
              <DashboardLayout><AdminTenantsPage /></DashboardLayout>
            </ProtectedRoute>
          } />
          <Route path="/admin/finance" element={
            <ProtectedRoute allowedRoles={["admin"]}>
              <DashboardLayout><AdminFinancePage /></DashboardLayout>
            </ProtectedRoute>
          } />
          <Route path="/admin/activity" element={
            <ProtectedRoute allowedRoles={["admin"]}>
              <DashboardLayout><AdminActivityPage /></DashboardLayout>
            </ProtectedRoute>
          } />
          <Route path="/admin/permission-templates" element={
            <ProtectedRoute allowedRoles={["admin"]}>
              <DashboardLayout><PermissionTemplatesPage /></DashboardLayout>
            </ProtectedRoute>
          } />
          <Route path="/admin/subscriptions" element={
            <ProtectedRoute allowedRoles={["admin"]}>
              <DashboardLayout><AdminSubscriptionPage /></DashboardLayout>
            </ProtectedRoute>
          } />
          <Route path="/admin/customers" element={
            <ProtectedRoute allowedRoles={["admin"]}>
              <DashboardLayout><AdminCustomersPage /></DashboardLayout>
            </ProtectedRoute>
          } />
          <Route path="/admin/tariffs" element={
            <ProtectedRoute allowedRoles={["admin"]}>
              <DashboardLayout><AdminTariffsPage /></DashboardLayout>
            </ProtectedRoute>
          } />
          <Route path="/admin/queue" element={
            <ProtectedRoute allowedRoles={["admin"]}>
              <DashboardLayout><QueuePage /></DashboardLayout>
            </ProtectedRoute>
          } />
          
          {/* Shared: Admin + Customer */}
          <Route path="/alerts" element={
            <ProtectedRoute allowedRoles={["admin", "customer", "staff"]}>
              <DashboardLayout><DeviceAlertsPage /></DashboardLayout>
            </ProtectedRoute>
          } />
          <Route path="/health" element={
            <ProtectedRoute allowedRoles={["admin", "customer", "staff"]}>
              <DashboardLayout><DeviceHealthPage /></DashboardLayout>
            </ProtectedRoute>
          } />
          
          <Route path="/invoices" element={
            <ProtectedRoute allowedRoles={["customer", "staff"]}>
              <DashboardLayout><InvoicesPage /></DashboardLayout>
            </ProtectedRoute>
          } />
          <Route path="/subscription" element={
            <ProtectedRoute allowedRoles={["customer"]}>
              <DashboardLayout><CustomerSubscriptionPage /></DashboardLayout>
            </ProtectedRoute>
          } />
          <Route path="/cash-handover" element={
            <ProtectedRoute allowedRoles={["customer", "staff"]}>
              <DashboardLayout><CashHandoverPage /></DashboardLayout>
            </ProtectedRoute>
          } />
          <Route path="/fixed-expenses" element={
            <ProtectedRoute allowedRoles={["customer", "staff"]}>
              <DashboardLayout><FixedExpensesPage /></DashboardLayout>
            </ProtectedRoute>
          } />
          {/* Customer (Apartman Sahibi) Routes */}
          <Route path="/dashboard" element={
            <ProtectedRoute allowedRoles={["customer", "staff"]}>
              <DashboardLayout><CustomerDashboard /></DashboardLayout>
            </ProtectedRoute>
          } />
          <Route path="/apartments" element={
            <ProtectedRoute allowedRoles={["customer", "staff", "admin"]}>
              <DashboardLayout><ApartmentsPage /></DashboardLayout>
            </ProtectedRoute>
          } />
          <Route path="/properties" element={
            <ProtectedRoute allowedRoles={["customer", "staff", "admin"]}>
              <DashboardLayout><PropertiesPage /></DashboardLayout>
            </ProtectedRoute>
          } />
          <Route path="/properties/:propertyId" element={
            <ProtectedRoute allowedRoles={["customer", "staff", "admin"]}>
              <DashboardLayout><PropertyDetailPage /></DashboardLayout>
            </ProtectedRoute>
          } />
          <Route path="/apartments/:apartmentId/units" element={
            <ProtectedRoute allowedRoles={["customer", "staff", "admin"]}>
              <DashboardLayout><UnitsPage /></DashboardLayout>
            </ProtectedRoute>
          } />
          <Route path="/devices" element={
            <ProtectedRoute allowedRoles={["customer", "staff", "admin"]}>
              <DashboardLayout><DevicesPage /></DashboardLayout>
            </ProtectedRoute>
          } />
          <Route path="/devices/:deviceId" element={
            <ProtectedRoute allowedRoles={["customer", "staff", "admin"]}>
              <DashboardLayout><DeviceDetailPage /></DashboardLayout>
            </ProtectedRoute>
          } />
          <Route path="/tenants" element={
            <ProtectedRoute allowedRoles={["customer", "staff", "admin"]}>
              <DashboardLayout><TenantsPage /></DashboardLayout>
            </ProtectedRoute>
          } />
          <Route path="/tariffs" element={
            <ProtectedRoute allowedRoles={["customer", "staff"]}>
              <DashboardLayout><TariffsPage /></DashboardLayout>
            </ProtectedRoute>
          } />
          <Route path="/settings" element={
            <ProtectedRoute allowedRoles={["customer"]}>
              <DashboardLayout><CustomerSettingsPage /></DashboardLayout>
            </ProtectedRoute>
          } />
          <Route path="/reports" element={
            <ProtectedRoute allowedRoles={["customer", "staff", "tenant"]}>
              <DashboardLayout><ReportsPage /></DashboardLayout>
            </ProtectedRoute>
          } />
          <Route path="/staff" element={
            <ProtectedRoute allowedRoles={["customer"]}>
              <DashboardLayout><StaffPage /></DashboardLayout>
            </ProtectedRoute>
          } />
          <Route path="/activity" element={
            <ProtectedRoute allowedRoles={["customer", "staff"]}>
              <DashboardLayout><CustomerActivityPage /></DashboardLayout>
            </ProtectedRoute>
          } />
          <Route path="/deleted-invoices" element={
            <ProtectedRoute allowedRoles={["customer", "staff", "admin"]}>
              <DashboardLayout><DeletedInvoicesPage /></DashboardLayout>
            </ProtectedRoute>
          } />
          
          {/* Tenant Routes */}
          <Route path="/tenant" element={
            <ProtectedRoute allowedRoles={["tenant"]}>
              <DashboardLayout><TenantDashboard /></DashboardLayout>
            </ProtectedRoute>
          } />
          <Route path="/topup" element={
            <ProtectedRoute allowedRoles={["tenant"]}>
              <DashboardLayout><TopupPage /></DashboardLayout>
            </ProtectedRoute>
          } />
          <Route path="/tenant/invoices" element={
            <ProtectedRoute allowedRoles={["tenant"]}>
              <DashboardLayout><TenantInvoicesPage /></DashboardLayout>
            </ProtectedRoute>
          } />
          
          {/* Shared Routes */}
          <Route path="/profile" element={
            <ProtectedRoute>
              <DashboardLayout><ProfilePage /></DashboardLayout>
            </ProtectedRoute>
          } />
          
          {/* Default Redirect */}
          <Route path="/" element={<Navigate to="/login" replace />} />
          <Route path="*" element={<Navigate to="/login" replace />} />
        </Routes>
      </BrowserRouter>
      <Toaster position="top-right" richColors />
    </AuthProvider>
  );
}

export default App;
